import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Camera, ImagePlus, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const MAX_FILES = 5;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

/**
 * Import a recipe from 1-5 photos via GPT-5.2 vision.
 * Supports gallery upload AND direct camera capture on mobile.
 */
export default function ImageImportDialog({ open, onOpenChange, servings = 6, onImported }) {
  const [files, setFiles] = useState([]); // [{ file, previewUrl }]
  const [mealType, setMealType] = useState("dinner");
  const [isBaking, setIsBaking] = useState(false);
  const [model, setModel] = useState(() => {
    try { return localStorage.getItem("preferred_image_import_model") || "gpt-5.2"; } catch { return "gpt-5.2"; }
  });
  const [loading, setLoading] = useState(false);
  // Preview state: when truthy, we show the editable review form instead of the upload form
  const [previewRecipe, setPreviewRecipe] = useState(null);
  const [saving, setSaving] = useState(false);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const addFiles = (fileList) => {
    const next = [...files];
    Array.from(fileList || []).forEach((f) => {
      if (!ALLOWED.includes((f.type || "").toLowerCase())) {
        toast.error(`Filtypen ${f.type || "ukjent"} ikkje støtta`);
        return;
      }
      if (next.length >= MAX_FILES) {
        toast.warning(`Maks ${MAX_FILES} bilder per oppskrift`);
        return;
      }
      next.push({ file: f, previewUrl: URL.createObjectURL(f) });
    });
    setFiles(next);
  };

  const removeAt = (idx) => {
    const next = [...files];
    URL.revokeObjectURL(next[idx]?.previewUrl);
    next.splice(idx, 1);
    setFiles(next);
  };

  const submit = async (modelOverride) => {
    if (files.length === 0) {
      toast.error("Legg til minst ett bilde");
      return;
    }
    const effectiveModel = modelOverride || model;
    setLoading(true);
    const formData = new FormData();
    files.forEach(({ file }) => formData.append("files", file));
    formData.append("servings", String(servings));
    formData.append("is_baking", String(isBaking));
    formData.append("meal_type", mealType);
    formData.append("model", effectiveModel);
    formData.append("preview", "true");
    try {
      const res = await api.importRecipeFromImages(formData);
      try { localStorage.setItem("preferred_image_import_model", effectiveModel); } catch { /* ignore */ }
      setModel(effectiveModel);
      // Show preview instead of closing immediately
      setPreviewRecipe(res.data);
      toast.success("Klar for gjennomgang — sjekk og rediger før du lagrer");
    } catch (err) {
      const detail = err.response?.data?.detail || "Feil ved bildeimport";
      if (err.response?.status === 422 && effectiveModel === "gpt-5.2") {
        toast.error(detail, {
          duration: 10000,
          action: {
            label: "Prøv Claude i staden",
            onClick: () => { setModel("claude-sonnet-4-6"); submit("claude-sonnet-4-6"); },
          },
        });
      } else if (err.response?.status === 422 && effectiveModel === "claude-sonnet-4-6") {
        toast.error(detail, {
          duration: 10000,
          action: {
            label: "Prøv Gemini",
            onClick: () => { setModel("gemini-3.1-pro"); submit("gemini-3.1-pro"); },
          },
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const savePreview = async () => {
    if (!previewRecipe) return;
    setSaving(true);
    try {
      // POST /recipes creates a new recipe from the user-edited payload
      const res = await api.createRecipe(previewRecipe);
      toast.success("Oppskrift lagret!");
      files.forEach((f) => URL.revokeObjectURL(f.previewUrl));
      setFiles([]);
      setPreviewRecipe(null);
      onOpenChange(false);
      onImported?.(res.data?.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved lagring");
    } finally {
      setSaving(false);
    }
  };

  const cancelPreview = () => {
    setPreviewRecipe(null);
  };

  const updatePreviewField = (key, value) => {
    setPreviewRecipe((r) => ({ ...r, [key]: value }));
  };

  const updateIngredient = (idx, key, value) => {
    setPreviewRecipe((r) => {
      const list = [...(r.ingredients || [])];
      list[idx] = { ...list[idx], [key]: value };
      return { ...r, ingredients: list };
    });
  };

  const removeIngredient = (idx) => {
    setPreviewRecipe((r) => ({ ...r, ingredients: (r.ingredients || []).filter((_, i) => i !== idx) }));
  };

  const addIngredient = () => {
    setPreviewRecipe((r) => ({ ...r, ingredients: [...(r.ingredients || []), { name: "", amount: "", unit: "stk", category: "Annet" }] }));
  };

  const updateInstruction = (idx, value) => {
    setPreviewRecipe((r) => {
      const list = [...(r.instructions || [])];
      list[idx] = value;
      return { ...r, instructions: list };
    });
  };

  const removeInstruction = (idx) => {
    setPreviewRecipe((r) => ({ ...r, instructions: (r.instructions || []).filter((_, i) => i !== idx) }));
  };

  const addInstruction = () => {
    setPreviewRecipe((r) => ({ ...r, instructions: [...(r.instructions || []), ""] }));
  };


  return (
    <Dialog open={open} onOpenChange={(v) => { if (!loading && !saving) onOpenChange(v); }}>
      <DialogContent className="max-w-md max-h-[85dvh] sm:max-h-[92vh] flex flex-col p-0" data-testid="image-import-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Camera className="h-5 w-5 text-navy-800" /> {previewRecipe ? "Sjekk og rediger" : "Importer fra bilde"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {previewRecipe
              ? "Endre titler, ingredienser eller steg før du lagrer."
              : "Last opp 1–5 bilder av oppskriften (kokebok, blad, skjermbilde). AI les og strukturerer."}
          </DialogDescription>
        </DialogHeader>

        {previewRecipe ? (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0 space-y-4" data-testid="preview-recipe-form">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">Tittel</label>
                <input
                  value={previewRecipe.title || ""}
                  onChange={(e) => updatePreviewField("title", e.target.value)}
                  className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none"
                  data-testid="preview-title-input"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">Beskrivelse</label>
                <textarea
                  value={previewRecipe.description || ""}
                  onChange={(e) => updatePreviewField("description", e.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none resize-none"
                  data-testid="preview-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">Prep</label>
                  <input
                    value={previewRecipe.prep_time || ""}
                    onChange={(e) => updatePreviewField("prep_time", e.target.value)}
                    className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none"
                    data-testid="preview-prep-input"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">Steketid</label>
                  <input
                    value={previewRecipe.cook_time || ""}
                    onChange={(e) => updatePreviewField("cook_time", e.target.value)}
                    className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none"
                    data-testid="preview-cook-input"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
                    Ingredienser ({(previewRecipe.ingredients || []).length})
                  </label>
                  <button
                    onClick={addIngredient}
                    type="button"
                    className="text-[11px] text-navy-800 hover:underline font-semibold"
                    data-testid="preview-add-ingredient"
                  >
                    + Legg til
                  </button>
                </div>
                <div className="space-y-1.5">
                  {(previewRecipe.ingredients || []).map((ing, i) => (
                    <div key={i} className="flex gap-1 items-center" data-testid={`preview-ingredient-${i}`}>
                      <input
                        value={ing.amount || ""}
                        onChange={(e) => updateIngredient(i, "amount", e.target.value)}
                        placeholder="2"
                        className="w-12 rounded border border-stone-200 bg-white px-1.5 py-1 text-xs focus:border-navy-800 outline-none"
                      />
                      <input
                        value={ing.unit || ""}
                        onChange={(e) => updateIngredient(i, "unit", e.target.value)}
                        placeholder="stk"
                        className="w-14 rounded border border-stone-200 bg-white px-1.5 py-1 text-xs focus:border-navy-800 outline-none"
                      />
                      <input
                        value={ing.name || ""}
                        onChange={(e) => updateIngredient(i, "name", e.target.value)}
                        placeholder="ingrediens"
                        className="flex-1 min-w-0 rounded border border-stone-200 bg-white px-1.5 py-1 text-xs focus:border-navy-800 outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => removeIngredient(i)}
                        className="text-stone-400 hover:text-red-600 p-1"
                        title="Fjern ingrediens"
                        data-testid={`preview-remove-ingredient-${i}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
                    Steg ({(previewRecipe.instructions || []).length})
                  </label>
                  <button
                    onClick={addInstruction}
                    type="button"
                    className="text-[11px] text-navy-800 hover:underline font-semibold"
                    data-testid="preview-add-step"
                  >
                    + Legg til
                  </button>
                </div>
                <div className="space-y-1.5">
                  {(previewRecipe.instructions || []).map((step, i) => (
                    <div key={i} className="flex gap-1 items-start" data-testid={`preview-step-${i}`}>
                      <span className="text-[11px] text-stone-400 pt-2 w-6 flex-shrink-0">{i + 1}.</span>
                      <textarea
                        value={step}
                        onChange={(e) => updateInstruction(i, e.target.value)}
                        rows={2}
                        className="flex-1 min-w-0 rounded border border-stone-200 bg-white px-2 py-1 text-xs focus:border-navy-800 outline-none resize-none"
                      />
                      <button
                        type="button"
                        onClick={() => removeInstruction(i)}
                        className="text-stone-400 hover:text-red-600 p-1 mt-1"
                        title="Fjern steg"
                        data-testid={`preview-remove-step-${i}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="border-t border-stone-100 px-5 py-3 bg-white flex gap-2 flex-shrink-0" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
              <button
                onClick={cancelPreview}
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center rounded-md border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-600 hover:bg-stone-50"
                data-testid="preview-back-btn"
              >
                Tilbake
              </button>
              <button
                onClick={savePreview}
                disabled={saving || !previewRecipe.title?.trim()}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                data-testid="preview-save-btn"
              >
                <Sparkles className="h-4 w-4" />
                {saving ? "Lagrer…" : "Lagre oppskrift"}
              </button>
            </div>
          </>
        ) : (
          <>
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          <div className="space-y-4">
            {/* Bilde-pickers */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => cameraInputRef.current?.click()}
                disabled={loading || files.length >= MAX_FILES}
                className="inline-flex flex-col items-center justify-center gap-1 py-4 rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 hover:border-navy-800 hover:bg-sailcloth-100/50 transition-colors disabled:opacity-40"
                data-testid="image-camera-btn"
              >
                <Camera className="h-5 w-5 text-navy-800" />
                <span className="text-xs font-semibold text-stone-700">Ta bilde</span>
              </button>
              <button
                onClick={() => galleryInputRef.current?.click()}
                disabled={loading || files.length >= MAX_FILES}
                className="inline-flex flex-col items-center justify-center gap-1 py-4 rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 hover:border-navy-800 hover:bg-sailcloth-100/50 transition-colors disabled:opacity-40"
                data-testid="image-gallery-btn"
              >
                <ImagePlus className="h-5 w-5 text-navy-800" />
                <span className="text-xs font-semibold text-stone-700">Velg fra galleri</span>
              </button>
            </div>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={(e) => addFiles(e.target.files)} className="hidden" />
            <input ref={galleryInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(e) => addFiles(e.target.files)} className="hidden" />

            {/* Thumbnails */}
            {files.length > 0 && (
              <div className="grid grid-cols-3 gap-2" data-testid="image-import-thumbs">
                {files.map((f, idx) => (
                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-stone-200 bg-stone-100">
                    <img src={f.previewUrl} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeAt(idx)}
                      disabled={loading}
                      className="absolute top-1 right-1 bg-white/90 hover:bg-red-600 hover:text-white p-1 rounded text-stone-700 transition-colors"
                      data-testid={`image-remove-${idx}`}
                      title="Fjern bilde"
                    >
                      <X className="h-3 w-3" />
                    </button>
                    <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                      {idx + 1}/{files.length}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-stone-400">
              {files.length}/{MAX_FILES} bilder · JPEG, PNG eller WEBP
            </p>

            <div className="border-t border-stone-100 pt-4 space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">Måltidstype</label>
                <Select value={mealType} onValueChange={setMealType}>
                  <SelectTrigger data-testid="image-meal-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="breakfast">Frokost</SelectItem>
                    <SelectItem value="lunch">Lunsj</SelectItem>
                    <SelectItem value="dinner">Middag</SelectItem>
                    <SelectItem value="baking">Baking</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm text-stone-600 cursor-pointer">
                <input type="checkbox" checked={isBaking} onChange={(e) => setIsBaking(e.target.checked)} className="rounded border-stone-300 h-4 w-4" data-testid="image-is-baking" />
                Baking-oppskrift
              </label>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">AI-modell</label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger data-testid="image-model-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-5.2">GPT-5.2 (best på norsk tekst)</SelectItem>
                    <SelectItem value="claude-sonnet-4-6">Claude Sonnet 4.6 (best på layout)</SelectItem>
                    <SelectItem value="gemini-3.1-pro">Gemini 3.1 Pro (billigst)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading && (
              <div className="space-y-3 py-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-2/3" />
                <p className="text-xs text-stone-400 text-center mt-2">Analyserer bilde(ne) med AI…</p>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-stone-100 px-5 py-3 bg-white">
          <button
            onClick={() => submit()}
            disabled={loading || files.length === 0}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-navy-800 px-4 py-3 text-sm font-semibold text-white hover:bg-navy-900 transition-colors disabled:opacity-50"
            data-testid="image-import-submit-btn"
          >
            <Sparkles className="h-4 w-4" />
            {loading ? "Henter ut oppskrift…" : `Forhåndsvis fra ${files.length || 0} bilde${files.length === 1 ? "" : "r"}`}
          </button>
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
